import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { DateDisplay } from "commonComponents/utilities/DateDisplay/DateDisplay";
import { NoteText } from "commonComponents/utilities/NoteText/NoteText";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";
import { StatusTagDisplay } from "commonComponents/utilities/Status/StatusTagDisplay";

import { type TakeoffUserDetails } from "entity/internalUser/InternalUserDetails/InternalUser.api.types";
import { BuildertrendTakeOffLogo } from "entity/takeoff/BuildertrendTakeOffLogo/BuildertrendTakeOffLogo";
import { TakeoffIntegrationMarketing } from "entity/takeoff/TakeoffIntegrationMarketing/TakeoffIntegrationMarketing";
import { type TakeoffSettingsFormActions } from "entity/takeoff/TakeoffSettings/TakeoffSettings.api.types";

const marketingTitle = "Streamline estimating with Buildertrend Takeoff";
const marketingDetails =
    "Your plan comes with Buildertrend Takeoff, a full-featured takeoff tool that integrates line items and costs with the Buildertrend estimates and job proposal experiences. You'll be able to upload your digital blueprints and get accurate measurements for your line items in seconds using Buildertrend Takeoff. To get started, we'll open a new browser window and automatically set up your Buildertrend Takeoff account. ";

interface ITakeoffIntegrationUserConnectedProps {
    takeoffUserDetails: TakeoffUserDetails;
    actionBeingPerformed: TakeoffSettingsFormActions;
    onDisconnect: () => void;
}

interface IInternalUserTakeoffTabProps extends ITakeoffIntegrationUserConnectedProps {
    onInitiateConnection: () => void;
    firstName: string;
    lastName: string;
}

const TakeoffIntegrationUserConnected: React.FC<ITakeoffIntegrationUserConnectedProps> = ({
    takeoffUserDetails,
    actionBeingPerformed,
    onDisconnect,
}) => {
    return (
        <>
            <BTRow>
                <BTCol xs={24}>
                    <BuildertrendTakeOffLogo height={72} />
                </BTCol>
            </BTRow>
            <BTRow className="margin-vertical-lg">
                <BTCol xs={24}>
                    <StatusTagDisplay statusType="success" statusText="Connected" />
                    <NoteText>
                        <span className="margin-left-sm">
                            Connected since{" "}
                            <DateDisplay value={takeoffUserDetails.connectionDate!} showTime />
                        </span>
                    </NoteText>
                </BTCol>
            </BTRow>
            {!takeoffUserDetails.isReadOnly && (
                <BTRow>
                    <BTCol xs={24}>
                        <BTButtonDeprecated
                            data-testid="disconnectTakeoff"
                            actionBeingPerformed={actionBeingPerformed}
                            onClick={onDisconnect}
                        >
                            Disconnect
                        </BTButtonDeprecated>
                    </BTCol>
                </BTRow>
            )}
        </>
    );
};

const InternalUserTakeoffTab: React.FC<IInternalUserTakeoffTabProps> = ({
    takeoffUserDetails,
    actionBeingPerformed,
    onInitiateConnection,
    onDisconnect,
    firstName,
    lastName,
}) => {
    const readOnlyDetails = (
        <>
            You do not have access to connect {firstName} {lastName} to Buildertrend Takeoff.{" "}
            {firstName} {lastName} can connect their own Buildertrend account to Buildertrend
            Takeoff through their user preferences.
        </>
    );
    const details = !takeoffUserDetails.canConnect ? readOnlyDetails : marketingDetails;
    return (
        <PageSection title="Buildertrend Takeoff">
            {!takeoffUserDetails.isConnected && (
                <TakeoffIntegrationMarketing
                    title={marketingTitle}
                    details={details}
                    showLearnMoreLink={false}
                    showConnectButton={takeoffUserDetails.canConnect}
                    onClick={onInitiateConnection}
                />
            )}
            {takeoffUserDetails.isConnected && (
                <TakeoffIntegrationUserConnected
                    takeoffUserDetails={takeoffUserDetails}
                    actionBeingPerformed={actionBeingPerformed}
                    onDisconnect={onDisconnect}
                />
            )}
        </PageSection>
    );
};

export default InternalUserTakeoffTab;
